import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EnterRouteFormProps, WaypointPredicate } from './types';
import { ArcCollapsibleSection, ArcIconButton, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from '@src/core/ui';
import { Controller, useFieldArray } from 'react-hook-form';
import { REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditableRoute, selectTerrainMode, selectWaypoints, setEditableRoute, setTerrainMode, setWaypoints, useGetTerrainAwareMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { setMapCenter, useConfirm } from '@src/core/controllers';
import { arcSpatiumUnit, arcToFixed, arcConvertBetweenUnitTypes, arcConvertUnit, arcVelocitasUnit } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { UnitType } from '@src/core/enums';
import { InputAdornment } from '@mui/material';
import TerrainAlert from '@src/features/order/pages/upsert-mission-page/components/terrain-alert';
import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { useNavigate } from 'react-router-dom';
import { useNumericRanges, useUnitType } from '@src/core/features/settings';
import {
  calculateDistance,
  interpolateAltitude,
  roundWaypointCoords,
  calculateMissionDeliveryAltitude,
} from '@src/features/order/pages/upsert-mission-page/utils';

const EnterRouteForm: React.FC<EnterRouteFormProps> = (props) => {
  const { form, onSubmit, isSubmitting, isCanceling, goBack, submitLabel = 'Next', embedded = false, hideActions = false } = props;
  const unitType = useUnitType();
  const prevUnitTypeRef = React.useRef<UnitType>(unitType);
  
  const { confirm } = useConfirm();
  const dispatch = useDispatch();
  const wps = useSelector(selectWaypoints);
  const editableRoute = useSelector(selectEditableRoute);
  const terrainMode = useSelector(selectTerrainMode);
  const isAmsl = terrainMode === TerrainMode.AMSL;
  const isAgl = terrainMode === TerrainMode.AGL;
  const isRelative = terrainMode === TerrainMode.RELATIVE;
  const deliveyMode = useGetSettingByUnitType(SettingUnitType.DELIVERY_MODE);
  const settingsFlightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const settingsDeliveryAltitude = useGetSettingByUnitType(SettingUnitType.DELIVERY_ALTITUDE);
  const numericRanges = useNumericRanges();

  const [, { data: terrainData = [] }] = useGetTerrainAwareMutation({ fixedCacheKey: terrainCacheKey });

  const terrainWithDist = React.useMemo(
    () => (terrainData.length > 0 ? calculateDistance(terrainData) : []),
    [terrainData],
  );
  const wpsWithDist = React.useMemo(
    () => (wps.length > 0 ? calculateDistance(wps) : []),
    [wps],
  );
  const wpsRef = React.useRef(wps);
  wpsRef.current = wps;

  const getTerrainAtWp = React.useCallback(
    (predicate: WaypointPredicate): number | null => {
      if (terrainWithDist.length === 0 || wpsWithDist.length === 0) return null;
      const wpWithDist = wpsWithDist.find((w) => predicate(w as RouteWaypointEntity));
      if (!wpWithDist) return null;
      return interpolateAltitude(wpWithDist.distance, terrainWithDist);
    },
    [terrainWithDist, wpsWithDist],
  );

  const takeoffTerrainAlt = React.useMemo(
    () => getTerrainAtWp((w) => w.type === RouteWaypointType.TAKE_OFF),
    [getTerrainAtWp],
  );
  const destinationTerrainAlt = React.useMemo(
    () => getTerrainAtWp((w) => w.type === RouteWaypointType.DESTINATION),
    [getTerrainAtWp],
  );

  // AMSL hint (AGL mode only):
  //   - RELATIVE / AMSL: no hint
  //   - AGL: drone's AMSL = localTerrain + input
  const renderAltitudeHint = React.useCallback(
    (value: string | number, localTerrainAlt: number | null) => {
      if (isAmsl || isRelative) return null;
      const num = Number(value);
      if (isNaN(num)) return null;

      const base = isAgl ? localTerrainAlt : null;
      if (base == null) return null;

      // base is SI terrain altitude; num is the display-unit input. Convert the
      // terrain to display before adding so the AMSL hint is correct for IMPERIAL.
      const baseDisplay = arcConvertUnit(base, MeasurementType.DISTANCE, unitType);

      return (
        <ArcText.h5 sx={{ whiteSpace: 'nowrap', mr: 0.5 }} color='text.secondary'>
          ≈ {Math.round(baseDisplay + num)} {arcSpatiumUnit()} (AMSL)
        </ArcText.h5>
      );
    },
    [isAmsl, isAgl, isRelative, unitType],
  );

  const { fields, replace } = useFieldArray({ control: form.control, name: 'waypoints' });

  const convertAltitudeValue = React.useCallback(
    (
      raw: string | number | undefined,
      fromUnit: UnitType,
      toUnit: UnitType,
      measurement: MeasurementType = MeasurementType.DISTANCE,
    ): string => {
      const num = Number(raw);
      if (!Number.isFinite(num)) return raw != null ? String(raw) : '';
      const converted = arcConvertBetweenUnitTypes(
        num,
        measurement,
        fromUnit,
        toUnit
      );
      return arcToFixed(converted, 1);
    },
    []
  );

  const syncDeliveryFromDestination = React.useCallback(
    (destinationValue: string | number) => {
      if (!isAmsl) return;

      // Settings values are SI; the destination form value is in display units.
      // Convert the settings to display so the offset arithmetic stays single-unit.
      const toDisplay = (v: number | string) =>
        arcConvertBetweenUnitTypes(Number(v), MeasurementType.DISTANCE, UnitType.SI, unitType);

      const delivery = calculateMissionDeliveryAltitude(
        destinationValue,
        toDisplay(settingsFlightAltitude.value),
        toDisplay(settingsDeliveryAltitude.value),
      );
      form.setValue('deliveryAltitude', delivery);
    },
    [form, isAmsl, settingsDeliveryAltitude.value, settingsFlightAltitude.value, unitType],
  );

  React.useEffect(() => {
    const prevUnit = prevUnitTypeRef.current;
    if (prevUnit === unitType) return;
    prevUnitTypeRef.current = unitType;

    dispatch(setWaypoints(wpsRef.current.map((w) => ({
      ...w,
      altitude: convertAltitudeValue(w.altitude, prevUnit, unitType),
    }))));

    const takeOff = form.getValues('takeOffAltitude');
    const destination = form.getValues('destinationAltitude');
    form.setValue('takeOffAltitude', convertAltitudeValue(takeOff, prevUnit, unitType));

    const convertedDestination = convertAltitudeValue(destination, prevUnit, unitType);
    form.setValue('destinationAltitude', convertedDestination);

    // Cruise speed (SPEED) and winch altitude always need converting; delivery
    // altitude is auto-derived in AMSL (syncDeliveryFromDestination) but is a free
    // field otherwise, so convert it directly when not AMSL.
    form.setValue('speed', convertAltitudeValue(form.getValues('speed'), prevUnit, unitType, MeasurementType.SPEED));
    form.setValue('winchAltitude', convertAltitudeValue(form.getValues('winchAltitude'), prevUnit, unitType));
    if (isAmsl) {
      syncDeliveryFromDestination(convertedDestination);
    } else {
      form.setValue('deliveryAltitude', convertAltitudeValue(form.getValues('deliveryAltitude'), prevUnit, unitType));
    }

    const waypointFields = form.getValues('waypoints') ?? [];
    waypointFields.forEach((_, index) => {
      const current = form.getValues(`waypoints.${index}.altitude`);
      form.setValue(
        `waypoints.${index}.altitude`,
        convertAltitudeValue(current, prevUnit, unitType)
      );
    });
  }, [unitType, convertAltitudeValue, dispatch, form, syncDeliveryFromDestination, isAmsl]);

  const pureWps = React.useMemo(() => {
    return wps.filter((w) => w.type === RouteWaypointType.WAYPOINT);
  }, [wps]);


  const onChangeAltitde = (value: string, predicate: WaypointPredicate) => {
    dispatch(setWaypoints((wps.map((w: RouteWaypointEntity) => {
      if (predicate(w)) {
        return {
          ...w,
          altitude: value,
        };
      }
      return w;
    }))));
  };

  const onDeleteWp = async (uuid: string) => {
    if (await confirm('Are you sure you want to delete this waypoint?', 'Delete Waypoint'))
      dispatch(setWaypoints(wps.filter((wp) => wp.uuid !== uuid)));
  };

  const syncAltitudeFormValues = (waypoints: RouteWaypointEntity[]) => {
    const takeOff = waypoints.find((w) => w.type === RouteWaypointType.TAKE_OFF);
    const destination = waypoints.find((w) => w.type === RouteWaypointType.DESTINATION);
    if (takeOff) form.setValue('takeOffAltitude', arcToFixed(takeOff.altitude));
    if (destination) form.setValue('destinationAltitude', arcToFixed(destination.altitude));
    waypoints
      .filter((w) => w.type === RouteWaypointType.WAYPOINT)
      .forEach((wp, index) => form.setValue(`waypoints.${index}.altitude`, arcToFixed(wp.altitude)));
  };

  const roundAltitudeOnBlur = (
    raw: string,
    field: { onChange: (value: string) => void; onBlur: () => void },
    onAltitudeChange: (value: string) => void,
  ) => {
    if (raw !== '' && raw !== undefined) {
      const num = Number(raw);
      if (!isNaN(num)) {
        const fixed = arcToFixed(num);
        if (fixed !== raw) {
          field.onChange(fixed);
          onAltitudeChange(fixed);
        }
      }
    }
    field.onBlur();
  };

  const toggleEditableRoute = () => {
    if (editableRoute) {
      const rounded = roundWaypointCoords(wps);
      dispatch(setWaypoints(rounded));
      syncAltitudeFormValues(rounded);
    }
    dispatch(setEditableRoute(!editableRoute));
  };

  React.useEffect(() => {
    if (pureWps.length === fields.length) return;

    replace(
      wps
        .filter((w) => w.type === RouteWaypointType.WAYPOINT)
        .map((w) => ({ ...w, altitude: arcToFixed(w.altitude) })),
    );
  }, [pureWps]);

  return (
    <ArcStack
      spacing={2}
      height={embedded ? 'auto' : '100%'}
      component='form'
      justifyContent={embedded ? 'flex-start' : 'space-between'}
      onSubmit={form.handleSubmit(onSubmit)}>
      <ArcStack spacing={2}>
        <ArcOutlinedSelect
          label='Altitude Mode'
          value={terrainMode}
          onChange={(e) => dispatch(setTerrainMode(e.target.value as TerrainMode))}>
          <ArcMenuItem value={TerrainMode.RELATIVE}>Relative to Launch</ArcMenuItem>
          <ArcMenuItem value={TerrainMode.AMSL}>AMSL</ArcMenuItem>
          <ArcMenuItem value={TerrainMode.AGL}>Above Terrain</ArcMenuItem>
        </ArcOutlinedSelect>

        <Controller 
          control={form.control} 
          name='takeOffAltitude' 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Takeoff Altitude'
              error={Boolean(errors.takeOffAltitude)}
              helperText={errors.takeOffAltitude?.message} 
              minNumber={numericRanges.flightAltitude?.min}
              maxNumber={numericRanges.flightAltitude?.max}
              onChange={(e) => {
                field.onChange(e);
                onChangeAltitde(e.target.value, (w) => w.type === RouteWaypointType.TAKE_OFF);
              }}
              onBlur={(e) => roundAltitudeOnBlur(
                e.target.value,
                field,
                (value: string) => onChangeAltitde(value, (w) => w.type === RouteWaypointType.TAKE_OFF),
              )}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {renderAltitudeHint(field.value, takeoffTerrainAlt)}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}}
            />
          )} />


      
        <Controller 
          control={form.control} 
          name='destinationAltitude'
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Destination Altitude'
              error={Boolean(errors.destinationAltitude)}
              helperText={errors?.destinationAltitude?.message}
              minNumber={numericRanges.flightAltitude?.min}
              maxNumber={numericRanges.flightAltitude?.max}
              onChange={(e) => {
                field.onChange(e);
                onChangeAltitde(e.target.value, (w) => w.type === RouteWaypointType.DESTINATION);
                syncDeliveryFromDestination(e.target.value);
              }}
              onBlur={(e) => roundAltitudeOnBlur(
                e.target.value,
                field,
                (value: string) => onChangeAltitde(value, (w) => w.type === RouteWaypointType.DESTINATION),
              )}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {renderAltitudeHint(field.value, destinationTerrainAlt)}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}}
            />
          )} />

        <ArcStack>
          {fields.map((wp, index) => {
            const name = `waypoints.${index}.altitude` as const;
            const wpTerrainAlt = getTerrainAtWp((w) => w.uuid === wp.uuid);
            return (
              <ArcStack key={wp.id} direction='row' spacing={1} mb={2}>
                <Controller 
                  name={name} 
                  key={wp.id} 
                  control={form.control} 
                  rules={{ required: REQUIRED_VALIDATION_TEXT }}
                  render={({ field, formState: { errors } }) => (
                    <ArcOutlinedInput 
                      {...field}
                      fullWidth
                      type='number'
                      label={`Waypoint Altitude ${index + 1}`}
                      error={Boolean(errors.waypoints?.[index]?.altitude)}
                      helperText={errors.waypoints?.[index]?.altitude?.message}
                      minNumber={numericRanges.flightAltitude?.min}
                      maxNumber={numericRanges.flightAltitude?.max}
                      onChange={(e) => {
                        field.onChange(e);
                        onChangeAltitde(e.target.value, (w) => w.uuid === wp.uuid);
                      }}
                      onBlur={(e) => roundAltitudeOnBlur(
                        e.target.value,
                        field,
                        (value) => onChangeAltitde(value, (w) => w.uuid === wp.uuid),
                      )}
                      onFocus={() => {
                        const lat = Number(wp.latitude);
                        const lng = Number(wp.longitude);

                        if (isNaN(lat) || isNaN(lng)) return;

                        dispatch(setMapCenter({ lat, lng }));
                      }}
                      slotProps={{ input: { 
                        endAdornment: (
                          <InputAdornment position='end'>
                            {renderAltitudeHint(field.value, wpTerrainAlt)}
                            <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                          </InputAdornment> 
                        )
                      }}}
                    />
                  )}/>
                {editableRoute &&
                <ArcIconButton sx={{ width: 40, height: 40 }} onClick={() => onDeleteWp(wp.uuid)}>
                  <DeleteOutlineIcon color='error' fontSize='small' />
                </ArcIconButton>
                }
              </ArcStack>
            );
          })}

          <ArcStack sx={{ pb: '16px' }}>
            <TerrainAlert />
          </ArcStack>
        </ArcStack>
        

        <Controller 
          control={form.control} 
          name='deliveryAltitude' 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label={isRelative ? 'Delivery Altitude (AGL)' : 'Delivery Altitude'}
              error={Boolean(errors.deliveryAltitude)}
              helperText={errors?.deliveryAltitude?.message}
              minNumber={numericRanges.deliveryAltitude?.min}
              maxNumber={numericRanges.deliveryAltitude?.max}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    {renderAltitudeHint(field.value, destinationTerrainAlt)}
                    <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                  </InputAdornment> 
                )
              }}} />
          )} />

        {isAmsl && (
          <Controller 
            control={form.control} 
            name='winchAltitude' 
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field, formState: { errors } }) => (
              <ArcOutlinedInput 
                {...field}
                type='number'
                label='Winch Altitude'
                error={Boolean(errors.winchAltitude)}
                helperText={errors?.winchAltitude?.message}
                minNumber={numericRanges.deliveryAltitude?.min}
                maxNumber={numericRanges.deliveryAltitude?.max}
                slotProps={{ input: { 
                  endAdornment: (
                    <InputAdornment position='end'> 
                      <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
                    </InputAdornment> 
                  )
                }}} />
            )} />
        )}

        <Controller 
          name='speed' 
          control={form.control} 
          rules={{ required: REQUIRED_VALIDATION_TEXT }}
          render={({ field, formState: { errors } }) => (
            <ArcOutlinedInput 
              {...field}
              type='number'
              label='Cruise Speed'
              error={Boolean(errors.speed)}
              helperText={errors?.speed?.message}
              minNumber={numericRanges.deliverySpeed?.min}
              maxNumber={numericRanges.deliverySpeed?.max}
              slotProps={{ input: { 
                endAdornment: (
                  <InputAdornment position='end'>
                    <ArcText.h5>{arcVelocitasUnit()}</ArcText.h5>
                  </InputAdornment> 
                )
              }}} />
          )} />


        <ArcCollapsibleSection title='MODE'>
          <Controller 
            control={form.control} 
            name='deliveryMode'
            rules={{ required: REQUIRED_VALIDATION_TEXT }}
            render={({ field, formState: { errors } }) => (
              <ArcOutlinedSelect
                {...field}
                label='Delivery Mode'
                error={Boolean(errors.deliveryMode)}
                helperText={errors?.deliveryMode?.message}>
                {deliveyMode.values?.map((v) => (
                  <ArcMenuItem 
                    key={v.value} 
                    value={v.value}> 
                    {v.name} 
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>
            )} />
        </ArcCollapsibleSection>
      </ArcStack>
    </ArcStack>
  );
};

export default EnterRouteForm;